import { useApi } from '../hooks/useApi.js';
import { fetchCalendar } from '../lib/api.js';
import { Seo } from '../components/Seo.jsx';
import { PageHeader } from '../components/PageHeader.jsx';
import { usePage } from '../hooks/usePage.js';

const FALLBACK = [
  { id: 1, kind: 'term',  title: 'Term 1',         startDate: '2025-08-10', endDate: '2025-12-05', notes: '17 weeks' },
  { id: 2, kind: 'break', title: 'Mid-term break', startDate: '2025-10-26', endDate: '2025-11-01', notes: '1 week' },
  { id: 3, kind: 'break', title: 'Winter break',   startDate: '2025-12-06', endDate: '2026-01-03', notes: '4 weeks' },
  { id: 4, kind: 'term',  title: 'Term 2',         startDate: '2026-01-04', endDate: '2026-04-10', notes: '14 weeks' },
  { id: 5, kind: 'break', title: 'Spring break',   startDate: '2026-04-11', endDate: '2026-04-24', notes: '2 weeks' },
  { id: 6, kind: 'exam',  title: 'Term 3 / Exams', startDate: '2026-04-25', endDate: '2026-06-27', notes: '9 weeks' },
  { id: 7, kind: 'break', title: 'Summer break',   startDate: '2026-06-28', endDate: '2026-08-09', notes: '6 weeks' },
];

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function parseLocalDate(iso) {
  if (!iso) return null;
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  return { y, m, d };
}

const fmtFull = (iso) => {
  const p = parseLocalDate(iso);
  if (!p) return '';
  return `${String(p.d).padStart(2, '0')} ${MONTHS_SHORT[p.m - 1]} ${p.y}`;
};

const KIND_LABEL = {
  term: 'Term',
  break: 'Break',
  exam: 'Exam',
  event: 'Event',
};

/**
 * Render an entry's title as raw HTML so admin-supplied titles can use
 * <em> for italic accents (e.g. "Term <em>1</em>"). Plain text passes
 * through unchanged.
 */
function rawTitle(entry) {
  const t = entry.title || '';
  // If the title contains "Term N", italicise the number to match the design language.
  return t.replace(/Term\s+(\d+(?:\s*\/\s*\w+)?)/i, 'Term <em>$1</em>');
}

function CalendarRow({ entry }) {
  const start = parseLocalDate(entry.startDate);
  const end = parseLocalDate(entry.endDate) || start;
  return (
    <div className="cal-row" data-kind={entry.kind}>
      <div className="cal-date">
        <span className="day">{start ? String(start.d).padStart(2, '0') : '—'}</span>
        <span className="month">{start ? MONTHS_SHORT[start.m - 1] : ''}</span>
        <span className="year">{start ? start.y : ''}</span>
      </div>
      <div className="cal-rail">
        <span className="cal-dot" aria-hidden="true" />
      </div>
      <div className="cal-card">
        <span className="cal-kind">{KIND_LABEL[entry.kind] || entry.kind}</span>
        <h3 className="cal-title" dangerouslySetInnerHTML={{ __html: rawTitle(entry) }} />
        <div className="cal-meta">
          <span>
            {fmtFull(entry.startDate)}
            {entry.endDate && entry.endDate !== entry.startDate && (
              <>
                <span className="arrow"> → </span>
                {fmtFull(entry.endDate)}
              </>
            )}
          </span>
          {entry.notes && <span className="duration">{entry.notes}</span>}
        </div>
      </div>
    </div>
  );
}

/**
 * If the timeline crosses a calendar year (e.g. 2025 → 2026), insert a
 * subtle band marking the year change between rows.
 */
function YearBand({ year }) {
  return (
    <div className="cal-year-divider" aria-hidden="true">
      <div className="label">{year}</div>
      <div className="rail" />
      <div><span className="badge">Academic year</span></div>
    </div>
  );
}

function buildRowsWithYearBands(items) {
  // Sort by startDate to be safe
  const sorted = [...items].sort((a, b) => String(a.startDate).localeCompare(String(b.startDate)));
  const rows = [];
  let prevYear = null;
  for (const entry of sorted) {
    const p = parseLocalDate(entry.startDate);
    const y = p?.y;
    if (y && y !== prevYear) {
      rows.push({ kind: 'band', year: y, key: `band-${y}` });
      prevYear = y;
    }
    rows.push({ kind: 'row', entry, key: `row-${entry.id}` });
  }
  return rows;
}

export function AcademicCalendarPage() {
  const head = usePage('calendar');
  const { data, loading } = useApi(fetchCalendar, []);
  const items = data && data.length ? data : FALLBACK;
  const rows = buildRowsWithYearBands(items);

  return (
    <>
      <Seo title="Academic Calendar" description={head.metaDescription} />

      <PageHeader
        crumbs={[{ label: 'Home', to: '/' }, { label: 'Academic Calendar' }]}
        headline={head.headline}
        lede={head.lede}
        image={head.banner}
      />

      <section>
        <div className="container">
          {loading ? (
            <p style={{ color: 'var(--ink-3)', textAlign: 'center' }}>Loading calendar…</p>
          ) : (
            <div className="cal-timeline">
              {rows.map((r) =>
                r.kind === 'band'
                  ? <YearBand key={r.key} year={r.year} />
                  : <CalendarRow key={r.key} entry={r.entry} />
              )}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
